import { useMemo, useState } from 'react';
import type { BreakdownRow } from '@/api/tracker-dashboard';
import { countryName, flagOf, fmtInt, fmtMoney } from './format';

type Basis = 'deps' | 'revenue' | 'clicks';

const BASES: { key: Basis; label: string }[] = [
  { key: 'deps', label: 'Deposits' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'clicks', label: 'Clicks' },
];

function value(r: { deps: number; clicks: number; payout_cents: number }, b: Basis): number {
  if (b === 'deps') return r.deps;
  if (b === 'clicks') return r.clicks;
  return r.payout_cents / 100;
}

function fmt(b: Basis, v: number): string {
  return b === 'revenue' ? fmtMoney(v) : fmtInt(v);
}

/**
 * The dashboard's one answer to "what changed". Ranked by absolute movement
 * rather than percentage: a geo that went from 1 deposit to 3 is +200% and worth
 * nothing, while one that went from 90 to 60 is the thing to look at this
 * morning. Rows are app × geo because that is the grain the traffic is actually
 * bought and broken at.
 */
export function TrackerMovers({
  rows,
  onDrill,
}: {
  rows: BreakdownRow[];
  onDrill: (row: BreakdownRow) => void;
}) {
  const [basis, setBasis] = useState<Basis>('deps');

  const { up, down } = useMemo(() => {
    const scored = rows
      .filter((r) => r.key !== '__other__')
      .map((r) => ({ row: r, change: value(r, basis) - value(r.prev, basis) }))
      // A pair that barely moved is noise in both directions.
      .filter((s) => s.change !== 0);
    const sorted = [...scored].sort((a, b) => b.change - a.change);
    return {
      up: sorted.filter((s) => s.change > 0).slice(0, 5),
      down: sorted.filter((s) => s.change < 0).slice(-5).reverse(),
    };
  }, [rows, basis]);

  const Column = ({
    title, items, tone,
  }: { title: string; items: { row: BreakdownRow; change: number }[]; tone: 'up' | 'down' }) => (
    <div>
      <div className="text-xs font-semibold text-content-subtle mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-xs text-content-subtle py-3">Nothing moved</div>
      ) : (
        <div className="space-y-1.5">
          {items.map(({ row, change }) => (
            <button
              key={row.key}
              onClick={() => onDrill(row)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-muted transition-colors text-left"
            >
              <span className="text-base leading-none shrink-0">{flagOf(row.sub ?? '')}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-content truncate">{row.label}</span>
                <span className="block text-[11px] text-content-subtle truncate">
                  {countryName(row.sub ?? '')} · {fmt(basis, value(row.prev, basis))} → {fmt(basis, value(row, basis))}
                </span>
              </span>
              <span className={`text-sm font-mono font-semibold shrink-0 ${tone === 'up' ? 'text-success' : 'text-danger'}`}>
                {change > 0 ? '+' : '−'}{fmt(basis, Math.abs(change))}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-content">What changed</h3>
          <p className="text-xs text-content-subtle mt-0.5">
            Biggest app × geo movement vs the previous period
          </p>
        </div>
        <div className="flex bg-surface-muted rounded-lg p-0.5 gap-0.5">
          {BASES.map((b) => (
            <button
              key={b.key}
              onClick={() => setBasis(b.key)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                basis === b.key ? 'bg-surface text-content shadow-sm' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Column title="Gained" items={up} tone="up" />
        <Column title="Lost" items={down} tone="down" />
      </div>
    </div>
  );
}
